import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Home, MessageSquare, Mic, BookOpen, Layers, LogOut, Flame, Sparkles, UserCircle, Trophy, BookMarked } from "lucide-react";

const NavItem = ({ to, icon: Icon, label, testId }) => {
  const location = useLocation();
  const active = location.pathname === to || (to !== "/app" && location.pathname.startsWith(to));
  return (
    <Link
      to={to}
      data-testid={testId}
      className={`flex items-center gap-3 px-4 py-3 rounded-2xl transition-all duration-200 ${
        active
          ? "bg-peach-500/15 text-peach-200 border border-peach-500/30"
          : "text-[#A89BC9] hover:bg-white/5 hover:text-white border border-transparent"
      }`}
    >
      <Icon className="h-5 w-5" />
      <span className="text-sm font-medium">{label}</span>
    </Link>
  );
};

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-navy-900 grain text-white">
      <div className="grid lg:grid-cols-[260px_1fr] min-h-screen">
        {/* Sidebar */}
        <aside className="hidden lg:flex flex-col gap-2 p-6 border-r border-white/5 bg-navy-800/40 sticky top-0 h-screen">
          <Link to="/app" className="flex items-center gap-3 mb-8" data-testid="sidebar-logo">
            <div className="h-10 w-10 rounded-2xl bg-navy-900 border border-white/10 flex items-center justify-center">
              <div className="france-shape h-6 w-6" />
            </div>
            <div>
              <div className="font-heading font-bold text-base leading-tight">Avoir la pêche</div>
            </div>
          </Link>

          <NavItem to="/app" icon={Home} label="Dashboard" testId="nav-dashboard" />
          <NavItem to="/app/chat" icon={MessageSquare} label="AI Chat" testId="nav-chat" />
          <NavItem to="/app/voice" icon={Mic} label="Speak" testId="nav-voice" />
          <NavItem to="/app/lessons" icon={BookOpen} label="Lessons" testId="nav-lessons" />
          <NavItem to="/app/flashcards" icon={Layers} label="Flashcards" testId="nav-flashcards" />
          <NavItem to="/app/challenge" icon={Trophy} label="Daily Challenge" testId="nav-challenge" />
          <NavItem to="/app/dictionary" icon={BookMarked} label="Dictionary" testId="nav-dictionary" />
          <NavItem to="/app/profile" icon={UserCircle} label="Profile" testId="nav-profile" />

          <div className="mt-auto space-y-3">
            {user && (
              <div className="glass rounded-2xl p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-10 w-10 rounded-xl bg-peach-500/20 border border-peach-500/30 flex items-center justify-center font-semibold text-peach-200">
                    {user.name?.[0]?.toUpperCase() || "U"}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold truncate" data-testid="sidebar-username">{user.name}</div>
                    <div className="text-xs text-[#A89BC9] truncate">{user.email}</div>
                  </div>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="flex items-center gap-1 text-peach-200" data-testid="sidebar-streak">
                    <Flame className="h-3.5 w-3.5" /> {user.streak || 0} d
                  </span>
                  <span className="flex items-center gap-1 text-peach-200" data-testid="sidebar-xp">
                    <Sparkles className="h-3.5 w-3.5" /> {user.xp || 0} XP
                  </span>
                  <span className="px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-white">
                    {user.level || "A1"}
                  </span>
                </div>
              </div>
            )}
            <button
              onClick={handleLogout}
              data-testid="logout-btn"
              className="w-full flex items-center gap-2 px-4 py-3 rounded-2xl text-[#A89BC9] hover:bg-white/5 hover:text-white transition-all"
            >
              <LogOut className="h-4 w-4" />
              <span className="text-sm">Logout</span>
            </button>
          </div>
        </aside>

        {/* Mobile top bar */}
        <div className="lg:hidden flex items-center justify-between p-4 border-b border-white/5 bg-navy-800/60 sticky top-0 z-30 backdrop-blur-md">
          <Link to="/app" className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-xl bg-navy-900 border border-white/10 flex items-center justify-center">
              <div className="france-shape h-5 w-5" />
            </div>
            <span className="font-heading font-bold">Avoir la pêche</span>
          </Link>
          <button onClick={handleLogout} className="p-2 rounded-xl bg-white/5" data-testid="logout-btn-mobile">
            <LogOut className="h-4 w-4" />
          </button>
        </div>

        {/* Main */}
        <main className="relative">
          {children}
        </main>

        {/* Mobile bottom nav */}
        <nav className="lg:hidden fixed bottom-0 left-0 right-0 grid grid-cols-5 bg-navy-800/95 backdrop-blur-lg border-t border-white/5 z-30">
          {[
            { to: "/app", icon: Home, label: "Home", testId: "mnav-dashboard" },
            { to: "/app/chat", icon: MessageSquare, label: "Chat", testId: "mnav-chat" },
            { to: "/app/voice", icon: Mic, label: "Speak", testId: "mnav-voice" },
            { to: "/app/dictionary", icon: BookMarked, label: "Dict", testId: "mnav-dictionary" },
            { to: "/app/flashcards", icon: Layers, label: "Cards", testId: "mnav-flashcards" },
          ].map(({ to, icon: Icon, label, testId }) => (
            <Link key={to} to={to} data-testid={testId} className="flex flex-col items-center gap-1 py-2 text-[10px] text-[#A89BC9] hover:text-peach-200">
              <Icon className="h-5 w-5" />
              {label}
            </Link>
          ))}
        </nav>
      </div>
    </div>
  );
}
